import { HttpEngine } from "@/App/services/httpClient/HttpEngine";
import { StatusCodes } from "http-status-codes";
import type { AxiosError, AxiosProgressEvent, AxiosResponse } from "axios";
import { HttpErrorParser } from "@/App/services/httpClient/HttpErrorParser";
import { FileType, type RequestBody } from "@/App/types/twiageTypes";
import { HumanReadableErrors } from "@/App/config/messages";
import { EventBus, EventType } from "@/App/services/eventBus";

type BackendErrorHandler = (error: AxiosError) => string;

const errorHandlers: Record<string, BackendErrorHandler> = {
  [StatusCodes.UNAUTHORIZED]: (): string => HumanReadableErrors.sessionExpired,
  [StatusCodes.FORBIDDEN]: (): string => forbiddenError({ resourceName: "resource" }),
  [StatusCodes.BAD_GATEWAY]: (): string => "TigerConnect servers are down or being upgraded.",
  [StatusCodes.SERVICE_UNAVAILABLE]: (): string =>
    "The TigerConnect servers are up, but overloaded with requests. Try again later.",
};

export const forbiddenError = ({ resourceName }: { resourceName: string }): string =>
  `You have no access to this ${resourceName}.`;

export enum TwiageErrorCode {
  unknown,
  forbidden = StatusCodes.FORBIDDEN,
}

export type TwiageRequestError = {
  code: number;
  message: string;
};

const tryParseHttpResponse = (error: AxiosError): string | undefined => {
  const message = HttpErrorParser.parse(error);
  console.log("Error/setError", message || "Error processing request.");
  return message;
};

const handleResponseError = (error: AxiosError): never => {
  const code = error.response?.status ?? TwiageErrorCode.unknown;
  let message = tryParseHttpResponse(error);
  const httpStatusCode = error.response?.status;

  if (code !== undefined && errorHandlers[code] !== undefined) {
    message = message ?? errorHandlers[code](error);

    if (httpStatusCode === StatusCodes.UNAUTHORIZED) {
      EventBus.emit({ eventType: EventType.onLogout, payload: message });
    }
  }

  throw { message: message ?? HumanReadableErrors.unknown, code };
};

export const extractData = <T>(response: AxiosResponse): T => response.data;

export const get = <Data>({
  path,
  headers,
  params,
  baseUrl = undefined,
}: {
  path: string;
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  baseUrl?: string;
}): Promise<Data> => {
  return HttpEngine.get(path, { baseURL: baseUrl, headers, params })
    .then(extractData<Data>)
    .catch(handleResponseError);
};

export const getBlob = ({
  path,
  query,
  fileType,
  baseUrl = undefined,
  headers = {},
}: {
  path: string;
  fileType: FileType;
  query?: Record<string, unknown>;
  baseUrl?: string;
  headers?: Record<string, string>;
}): Promise<string> => {
  return HttpEngine.get(path, {
    baseURL: baseUrl,
    responseType: "blob",
    params: query,
    headers,
  })
    .then(extractData<Blob>)
    .then((data) => {
      return URL.createObjectURL(new Blob([data], { type: fileType.toString() }));
    })
    .catch(handleResponseError);
};

export const post = <Data>({
  path,
  body = {},
  headers = {},
  query = undefined,
  baseUrl = undefined,
  onUploadProgress,
  abortController,
}: {
  path: string;
  body?: RequestBody | FormData;
  headers?: Record<string, string>;
  query?: Record<string, unknown>;
  baseUrl?: string;
  abortController?: AbortController;
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void;
}): Promise<Data> => {
  return HttpEngine.post(path, body, {
    baseURL: baseUrl,
    headers,
    params: query,
    signal: abortController?.signal,
    onUploadProgress,
  })
    .then(extractData<Data>)
    .catch(handleResponseError);
};

export const patch = <Data>({
  path,
  body,
  headers = {},
  params = undefined,
}: {
  path: string;
  params?: Record<string, string | boolean>;
  body: RequestBody | Array<RequestBody>;
  headers?: Record<string, string>;
}): Promise<Data> => {
  return HttpEngine.patch(path, body, { params, headers })
    .then(extractData<Data>)
    .catch(handleResponseError);
};

export const put = <Data>({ path, body = undefined }: { path: string; body?: RequestBody }): Promise<Data> => {
  return HttpEngine.put(path, body)
    .then(extractData<Data>)
    .catch(handleResponseError);
};

export const del = <Data>({
  path,
  body = undefined,
  headers = {},
  baseUrl = undefined,
  params = undefined,
}: {
  path: string;
  body?: RequestBody;
  headers?: Record<string, string>;
  params?: Record<string, unknown>;
  baseUrl?: string;
}): Promise<Data> => {
  return HttpEngine.delete(path, { baseURL: baseUrl, data: body, headers, params })
    .then(extractData<Data>)
    .catch(handleResponseError);
};

export const HttpClient = {
  get,
  getBlob,
  post,
  patch,
  put,
  delete: del,
};
